import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import {
  SquaresFour as LayoutDashboard,
  Briefcase,
  Gavel,
  BookOpen,
  CalendarBlank as Calendar,
  Users,
  Scales as Scale,
  IdentificationCard as UserCheck,
  Buildings as Building2,
  Tag,
  FileText,
  ListChecks as ListTodo,
  ChatCircle as MessageSquare,
  Clock as ClockIcon,
  ClockCounterClockwise,
  Archive,
  Wallet,
  Bank,
  ChartBar as BarChart3,
  Gauge,
  Globe,
  GearSix as Settings,
  CaretLeft as ChevronLeft,
  Kanban,
  Tray,
  ShieldWarning,
  Envelope,
  Warning,
  User,
  UserSwitch,
  CheckCircle,
  ShieldCheck,
  Scroll,
  UsersThree,
  Percent,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/store';
import { useSidebarStore } from './sidebarStore';
import { useClerkWorkspace, type Scope } from '@/lib/useClerkWorkspace';
import { useClerkWorkspaceStore, type PermissionCode } from '@/lib/clerkWorkspaceStore';
import { useApiBarristersStore } from '@/lib/barristersApi';
import { useChambersMode, workspaceTypeLabel } from '@/lib/useChambersMode';
import { canLogActivity } from '@/lib/activityAccess';
import { canAdministerGovernance } from '@/lib/governanceAccess';
import {
  canManageOwnClerks,
  canUseMatterPipeline,
  canUseJudgments,
  canUseChambersPolicies,
} from '@/lib/pupilUiBoundary';

interface NavItem {
  label: string;
  /** If set, this nav item is only visible when the clerk has this scope */
  scope?: Scope;
  icon: React.ReactNode;
  to: string;
  badge?: string;
  /** Opens in a new browser tab instead of navigating within the SPA */
  external?: boolean;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

/**
 * Intake and Reports inside the BARRISTER navs.
 *
 * The API refuses both to pupils and door tenants — `INTAKE_ROLES` (intake.routes.ts) and
 * `REPORT_ROLES` (reports.routes.ts) are both admin · senior_clerk · junior_clerk · silk ·
 * junior, so of the four barrister member types only silk and junior qualify. Offering the
 * links anyway produced two dead destinations: Intake answered 403 and rendered as an empty
 * inbox, Reports as "figures may be incomplete — please retry", a retry that can never succeed.
 *
 * This mirrors the server's list; it grants nothing. The server stays authoritative.
 */
const BARRISTER_INTAKE_REPORT_TYPES = ['silk', 'junior'];
const canUseIntakeAndReports = (memberType: string | undefined) =>
  BARRISTER_INTAKE_REPORT_TYPES.includes(memberType ?? '');

/**
 * Recovery (the overdue-fee chase worklist) inside the BARRISTER navs.
 *
 * `RECOVERY_ROLES` (fee-notes.routes.ts) is admin · senior_clerk · junior_clerk · silk · junior,
 * so of the barrister member types only silk and junior may call
 * `GET /fee-notes/recovery-workspace`. Pupils and door tenants have no billing authority and must
 * not see receivables at all.
 *
 * The page was clerk-only, which left a solo barrister — who has no clerk — with no worklist for
 * their own unpaid fee notes. The rows are scoped per caller by
 * `getAccessibleMatterIds(caller, chambersId, 'fees')` (fee-notes.service.ts), so a barrister sees
 * only their own matters' debt in either workspace type.
 *
 * This mirrors the server's list; it grants nothing. The server stays authoritative.
 */
const BARRISTER_RECOVERY_TYPES = ['silk', 'junior'];
const canUseRecovery = (memberType: string | undefined) =>
  BARRISTER_RECOVERY_TYPES.includes(memberType ?? '');

/**
 * Activity Log inside the BARRISTER navs.
 *
 * `AUDIT_ROLES` (audit-log.routes.ts) is admin · senior_clerk · junior_clerk · silk · junior ·
 * pupil · staff — every member type EXCEPT `door_tenant`. Both barrister navs carried the item
 * unconditionally, so a door tenant was sent to a page whose only request the server refuses.
 * Note this is NOT the pupil boundary: a pupil IS served the audit log (scoped to their own
 * activity), a door tenant is not served it at all.
 *
 * What a door tenant should be permitted is OD-07 / KG-SEC-37 and is deliberately NOT decided
 * here. This mirrors the server's list as it stands and grants nothing; the day OD-07 is
 * answered the server list changes and this gate must change with it — the cross-file guard in
 * `solo-barrister-panel.guard.test.ts` fails if the two drift apart.
 */
const BARRISTER_ACTIVITY_LOG_TYPES = ['silk', 'junior', 'pupil'];
const canUseActivityLog = (memberType: string | undefined) =>
  BARRISTER_ACTIVITY_LOG_TYPES.includes(memberType ?? '');

/**
 * My Clerks inside the BARRISTER navs.
 *
 * `/me/clerk-access` applies `OWNER_ROLES = silk · junior` to its WHOLE router
 * (clerk-access.routes.ts:11-12), so a door tenant is refused every call that page makes.
 * `canManageOwnClerks` withholds the item from a pupil as a ratified capability decision; it
 * says nothing about a door tenant, which left that case open. Both gates stay: one states the
 * pupil decision, this one mirrors the server. Same OD-07 caveat as above.
 */
const BARRISTER_OWN_CLERK_TYPES = ['silk', 'junior'];
const canUseOwnClerkAccess = (memberType: string | undefined) =>
  BARRISTER_OWN_CLERK_TYPES.includes(memberType ?? '');

function getNavForRole(memberType: string | undefined): NavSection[] {
  if (memberType === 'admin' || memberType === 'staff') {
    return [
      {
        items: [
          { label: 'Dashboard', icon: <LayoutDashboard className="h-[18px] w-[18px]" />, to: '/admin' },
          { label: 'Operations Board', icon: <Gauge className="h-[18px] w-[18px]" />, to: '/admin/operations-board' },
          { label: 'Bookings', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/admin/bookings' },
          { label: 'Intake Inbox', icon: <Tray className="h-[18px] w-[18px]" />, to: '/admin/intake' },
          { label: 'Conflict Reviews', icon: <ShieldWarning className="h-[18px] w-[18px]" />, to: '/admin/conflict-reviews' },
          { label: 'Matters', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/admin/matters' },
          { label: 'Pipeline', icon: <Kanban className="h-[18px] w-[18px]" />, to: '/admin/matters/pipeline' },
          { label: 'Hearings', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/admin/hearings' },
          { label: 'Judgments', icon: <BookOpen className="h-[18px] w-[18px]" />, to: '/admin/judgments' },
          { label: 'Calendar', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/admin/calendar' },
          { label: 'Diary Policy', icon: <ClockIcon className="h-[18px] w-[18px]" />, to: '/admin/settings/diary-policy' },
        ],
      },
      {
        label: 'People',
        items: [
          { label: 'Barristers', icon: <Scale className="h-[18px] w-[18px]" />, to: '/admin/barristers' },
          { label: 'Availability', icon: <CheckCircle className="h-[18px] w-[18px]" />, to: '/admin/availability-approvals' },
          { label: 'Clerk Cover', icon: <UserSwitch className="h-[18px] w-[18px]" />, to: '/admin/cover' },
          { label: 'Members', icon: <UserCheck className="h-[18px] w-[18px]" />, to: '/admin/members' },
          // Admin and staff share this branch, but `requireChambersAdmin` is an EXACT check on
          // `admin` and the governance routers apply it to GET as well — so staff were refused on
          // page LOAD and every refusal wrote a `permission_denied` row into the compliance
          // record. Staff still meet their own obligations through /me/policies. See
          // lib/governanceAccess.ts.
          ...(canAdministerGovernance(memberType)
            ? [
                { label: 'Policies', icon: <ShieldCheck className="h-[18px] w-[18px]" />, to: '/admin/policies' },
                { label: 'Constitution', icon: <Scroll className="h-[18px] w-[18px]" />, to: '/admin/constitution' },
                { label: 'Committees', icon: <UsersThree className="h-[18px] w-[18px]" />, to: '/admin/committees' },
              ]
            : []),
          { label: 'Information Barriers', icon: <Scale className="h-[18px] w-[18px]" />, to: '/admin/ethical-walls' },
          { label: 'Chambers', icon: <Building2 className="h-[18px] w-[18px]" />, to: '/admin/clerks' },
          { label: 'Clients', icon: <Users className="h-[18px] w-[18px]" />, to: '/admin/contacts' },
        ],
      },
      {
        label: 'Workflow',
        items: [
          { label: 'Documents', icon: <FileText className="h-[18px] w-[18px]" />, to: '/admin/documents' },
          { label: 'Tasks', icon: <ListTodo className="h-[18px] w-[18px]" />, to: '/admin/tasks' },
          // Admin and staff share this branch, but `staff` is on NO role list in
          // activities.routes.ts — every call answers 403. The item sent them to a page that
          // could only ever print the server's refusal. Same reasoning as the solo nav note.
          ...(canLogActivity(memberType)
            ? [{ label: 'Time & Expenses', icon: <ClockIcon className="h-[18px] w-[18px]" />, to: '/admin/activities' }]
            : []),
          { label: 'Communications', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/admin/communications' },
          { label: 'Fees & Billing', icon: <Wallet className="h-[18px] w-[18px]" />, to: '/admin/fees' },
          { label: 'Financial Policy', icon: <Percent className="h-[18px] w-[18px]" />, to: '/admin/settings/financial-policy' },
          { label: 'Bank Accounts', icon: <Bank className="h-[18px] w-[18px]" />, to: '/admin/bank-accounts' },
        ],
      },
      {
        label: 'Master data',
        items: [
          { label: 'Courts & Judges', icon: <UserCheck className="h-[18px] w-[18px]" />, to: '/admin/courts' },
          { label: 'Practice Areas', icon: <Tag className="h-[18px] w-[18px]" />, to: '/admin/practice-areas' },
        ],
      },
      {
        label: 'Insight',
        items: [
          { label: 'Reports', icon: <BarChart3 className="h-[18px] w-[18px]" />, to: '/admin/reports' },
          // The route existed and the barrister nav linked it, but admin and clerk had no entry —
          // the audit trail was reachable only by typing the URL. Note this is NOT 'Activities'
          // under Workflow: that page is billable time records. Same word, different thing.
          { label: 'Activity Log', icon: <ClockCounterClockwise className="h-[18px] w-[18px]" />, to: '/admin/activity-log' },
        ],
      },
      {
        label: 'System',
        items: [
          // RC-10: 'Public Site' and 'Integrations' hidden from nav — both are "coming soon" shells.
          { label: 'Support', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/me/support' },
          { label: 'Settings', icon: <Settings className="h-[18px] w-[18px]" />, to: '/admin/settings' },
        ],
      },
    ];
  }

  if (memberType === 'solicitor') {
    return [
      {
        items: [
          { label: 'Dashboard', icon: <LayoutDashboard className="h-[18px] w-[18px]" />, to: '/solicitor' },
          { label: 'My Matters', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/solicitor/matters' },
        ],
      },
      {
        label: 'Workflow',
        items: [
          { label: 'Documents', icon: <FileText className="h-[18px] w-[18px]" />, to: '/solicitor/documents' },
          { label: 'Calendar', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/solicitor/calendar' },
          { label: 'Fee Notes', icon: <Wallet className="h-[18px] w-[18px]" />, to: '/solicitor/fees' },
          { label: 'Messages', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/solicitor/messages' },
        ],
      },
    ];
  }

  if (memberType === 'senior_clerk' || memberType === 'junior_clerk') {
    return [
      {
        items: [
          { label: 'Operations Centre', icon: <Gauge className="h-[18px] w-[18px]" />, to: '/clerk/operations' },
          { label: 'Dashboard', icon: <LayoutDashboard className="h-[18px] w-[18px]" />, to: '/clerk' },
          { label: 'Control Tower', icon: <Gauge className="h-[18px] w-[18px]" />, to: '/clerk/control-tower', scope: 'Diary' as Scope },
          { label: 'Coverage', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/clerk/coverage', scope: 'Diary' as Scope },
          { label: 'Court Board', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/clerk/court-board', scope: 'Diary' as Scope },
          { label: 'Operations Board', icon: <Gauge className="h-[18px] w-[18px]" />, to: '/clerk/operations-board', scope: 'Diary' as Scope },
          { label: 'Master Diary', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/clerk/diary', scope: 'Diary' as Scope },
          { label: 'Bookings', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/clerk/bookings', scope: 'Bookings' as Scope },
        ],
      },
      {
        label: 'Matters & hearings',
        items: [
          { label: 'Intake Inbox', icon: <Tray className="h-[18px] w-[18px]" />, to: '/clerk/intake', scope: 'Matters' as Scope },
          { label: 'Conflict Reviews', icon: <ShieldWarning className="h-[18px] w-[18px]" />, to: '/clerk/conflict-reviews', scope: 'Matters' as Scope },
          { label: 'Matters', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/clerk/matters', scope: 'Matters' as Scope },
          { label: 'Pipeline', icon: <Kanban className="h-[18px] w-[18px]" />, to: '/clerk/matters/pipeline', scope: 'Matters' as Scope },
          { label: 'Hearings', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/clerk/hearings', scope: 'Matters' as Scope },
          { label: 'Judgments', icon: <BookOpen className="h-[18px] w-[18px]" />, to: '/clerk/judgments', scope: 'Matters' as Scope },
          { label: 'Archive', icon: <Archive className="h-[18px] w-[18px]" />, to: '/clerk/archive', scope: 'Matters' as Scope },
          { label: 'Schedule', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/clerk/schedule', scope: 'Diary' as Scope },
        ],
      },
      {
        label: 'People',
        items: [
          { label: 'My Barristers', icon: <User className="h-[18px] w-[18px]" />, to: '/clerk/my-barristers' },
          { label: 'Barristers', icon: <Scale className="h-[18px] w-[18px]" />, to: '/clerk/barristers' },
          { label: 'Availability', icon: <CheckCircle className="h-[18px] w-[18px]" />, to: '/clerk/availability-approvals' },
          { label: 'Clerk Cover', icon: <UserSwitch className="h-[18px] w-[18px]" />, to: '/clerk/cover' },
          { label: 'Chambers Policies', icon: <ShieldCheck className="h-[18px] w-[18px]" />, to: '/me/policies' },
          { label: 'Clients', icon: <Users className="h-[18px] w-[18px]" />, to: '/clerk/contacts' },
        ],
      },
      {
        label: 'Workflow',
        items: [
          { label: 'Documents', icon: <FileText className="h-[18px] w-[18px]" />, to: '/clerk/documents', scope: 'Documents' as Scope },
          { label: 'Tasks', icon: <ListTodo className="h-[18px] w-[18px]" />, to: '/clerk/tasks', scope: 'Tasks' as Scope },
          { label: 'Time & Expenses', icon: <ClockIcon className="h-[18px] w-[18px]" />, to: '/clerk/activities' },
          { label: 'Communications', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/clerk/communications', scope: 'Communications' as Scope },
          { label: 'Fees & Billing', icon: <Wallet className="h-[18px] w-[18px]" />, to: '/clerk/fees', scope: 'Fees' as Scope },
          { label: 'Recovery', icon: <Warning className="h-[18px] w-[18px]" />, to: '/clerk/recovery', scope: 'Fees' as Scope },
          { label: 'Bank Accounts', icon: <Bank className="h-[18px] w-[18px]" />, to: '/clerk/bank-accounts' },
        ],
      },
      {
        label: 'Insight',
        items: [
          { label: 'Reports', icon: <BarChart3 className="h-[18px] w-[18px]" />, to: '/clerk/reports' },
          // Scoped server-side to the barristers this clerk supports — see audit-log.service.ts.
          { label: 'Activity Log', icon: <ClockCounterClockwise className="h-[18px] w-[18px]" />, to: '/clerk/activity-log' },
        ],
      },
      {
        label: 'Profile',
        items: [
          { label: 'Support', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/me/support' },
        ],
      },
    ];
  }

  // Default barrister navigation (chambers mode)
  return [
    {
      items: [
        { label: 'My Day', icon: <LayoutDashboard className="h-[18px] w-[18px]" />, to: '/barrister' },
        { label: 'My Diary', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/barrister/diary' },
        { label: 'Court Day', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/barrister/court-day' },
      ],
    },
    {
      label: 'My practice',
      items: [
        ...(canUseIntakeAndReports(memberType)
          ? [{ label: 'Intake', icon: <Tray className="h-[18px] w-[18px]" />, to: '/barrister/intake' }]
          : []),
        // Silks decide mid-matter conflict reviews (API: conflicts/reviews allows silk). The route
        // was registered for barristers but never had a way in.
        ...(memberType === 'silk'
          ? [{ label: 'Conflict Reviews', icon: <ShieldWarning className="h-[18px] w-[18px]" />, to: '/barrister/conflict-reviews' }]
          : []),
        { label: 'My Matters', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/barrister/matters' },
        // Pipeline and Judgments are not among the twelve ratified Pupil capabilities. The server
        // still serves both to a pupil (a `case.read` board; `judgment.read.any`) — these hide the
        // nav item only, they are not security. See pupilUiBoundary's approved-scope section.
        ...(canUseMatterPipeline(memberType)
          ? [{ label: 'Pipeline', icon: <Kanban className="h-[18px] w-[18px]" />, to: '/barrister/matters/pipeline' }]
          : []),
        { label: 'My Hearings', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/barrister/hearings' },
        ...(canUseJudgments(memberType)
          ? [{ label: 'Judgments', icon: <BookOpen className="h-[18px] w-[18px]" />, to: '/barrister/judgments' }]
          : []),
        ...(canUseActivityLog(memberType)
          ? [{ label: 'Activity Log', icon: <ClockCounterClockwise className="h-[18px] w-[18px]" />, to: '/barrister/activity-log' }]
          : []),
        // `/me/clerk-access` is OWNER_ROLES = silk · junior, so a pupil got 403 and an empty
        // permissions page. Same reasoning as Intake/Reports above; the server stays authoritative.
        ...(canManageOwnClerks(memberType) && canUseOwnClerkAccess(memberType)
          ? [{ label: 'My Clerks', icon: <Users className="h-[18px] w-[18px]" />, to: '/barrister/my-clerks' }]
          : []),
        // Governance acknowledgement is not a Pupil capability. Server access is unchanged — the
        // policy routes still serve a pupil; only the nav entry is withheld.
        ...(canUseChambersPolicies(memberType)
          ? [{ label: 'Chambers Policies', icon: <ShieldCheck className="h-[18px] w-[18px]" />, to: '/me/policies' }]
          : []),
      ],
    },
    {
      label: 'Workflow',
      items: [
        { label: 'Clients', icon: <Users className="h-[18px] w-[18px]" />, to: '/barrister/clients' },
        { label: 'Documents', icon: <FileText className="h-[18px] w-[18px]" />, to: '/barrister/documents' },
        { label: 'Tasks', icon: <ListTodo className="h-[18px] w-[18px]" />, to: '/barrister/tasks' },
        { label: 'Communications', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/barrister/communications' },
        { label: 'Inbox', icon: <Envelope className="h-[18px] w-[18px]" />, to: '/barrister/inbox' },
        { label: 'Email Settings', icon: <Settings className="h-[18px] w-[18px]" />, to: '/barrister/inbox/settings' },
      ],
    },
    {
      label: 'Money',
      items: [
        { label: 'Time & Expenses', icon: <ClockIcon className="h-[18px] w-[18px]" />, to: '/barrister/activities' },
        { label: 'Fees', icon: <Wallet className="h-[18px] w-[18px]" />, to: '/barrister/fees' },
        ...(canUseRecovery(memberType)
          ? [{ label: 'Recovery', icon: <Warning className="h-[18px] w-[18px]" />, to: '/barrister/recovery' }]
          : []),
        { label: 'Bank Accounts', icon: <Bank className="h-[18px] w-[18px]" />, to: '/barrister/bank-accounts' },
        ...(canUseIntakeAndReports(memberType)
          ? [{ label: 'Reports', icon: <BarChart3 className="h-[18px] w-[18px]" />, to: '/barrister/reports' }]
          : []),
      ],
    },
    {
      label: 'Profile',
      items: [
        { label: 'Public Profile', icon: <Globe className="h-[18px] w-[18px]" />, to: '/barrister/profile' },
        { label: 'Availability', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/barrister/availability' },
        { label: 'Settings', icon: <Settings className="h-[18px] w-[18px]" />, to: '/barrister/settings' },
        { label: 'Support', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/me/support' },
      ],
    },
  ];
}

/**
 * Simplified navigation for solo barristers.
 * Removes chambers-specific features and focuses on personal productivity.
 */
function getSoloBarristerNav(memberType: string | undefined): NavSection[] {
  return [
    {
      items: [
        { label: 'Today', icon: <LayoutDashboard className="h-[18px] w-[18px]" />, to: '/barrister' },
        { label: 'Diary', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/barrister/diary' },
        { label: 'Court Day', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/barrister/court-day' },
        ...(canUseIntakeAndReports(memberType)
          ? [{ label: 'Intake', icon: <Tray className="h-[18px] w-[18px]" />, to: '/barrister/intake' }]
          : []),
        ...(memberType === 'silk'
          ? [{ label: 'Conflict Reviews', icon: <ShieldWarning className="h-[18px] w-[18px]" />, to: '/barrister/conflict-reviews' }]
          : []),
        { label: 'Matters', icon: <Briefcase className="h-[18px] w-[18px]" />, to: '/barrister/matters' },
        ...(canUseMatterPipeline(memberType)
          ? [{ label: 'Pipeline', icon: <Kanban className="h-[18px] w-[18px]" />, to: '/barrister/matters/pipeline' }]
          : []),
        { label: 'Hearings', icon: <Gavel className="h-[18px] w-[18px]" />, to: '/barrister/hearings' },
        // Judgments is NOT a chambers-coordination feature — a solo barrister records and cites
        // judgments like any other, and the API serves them to silk and junior for read and write
        // (judgments.routes.ts). The solo nav simply never carried the item, which left
        // /barrister/judgments and /barrister/judgments/new with no way in at all: a detail page
        // could only be reached by stumbling on it through search or the activity log. Same pupil
        // gate as the chambers nav above.
        ...(canUseJudgments(memberType)
          ? [{ label: 'Judgments', icon: <BookOpen className="h-[18px] w-[18px]" />, to: '/barrister/judgments' }]
          : []),
        ...(canUseActivityLog(memberType)
          ? [{ label: 'Activity Log', icon: <ClockCounterClockwise className="h-[18px] w-[18px]" />, to: '/barrister/activity-log' }]
          : []),
        // `/me/clerk-access` is OWNER_ROLES = silk · junior, so a pupil got 403 and an empty
        // permissions page. Same reasoning as Intake/Reports above; the server stays authoritative.
        ...(canManageOwnClerks(memberType) && canUseOwnClerkAccess(memberType)
          ? [{ label: 'My Clerks', icon: <Users className="h-[18px] w-[18px]" />, to: '/barrister/my-clerks' }]
          : []),
        // Chambers Policies is chambers-governance only — intentionally NOT shown to a
        // solo/single-firm barrister, who is not a member of a policy-issuing chambers.
      ],
    },
    {
      label: 'Work',
      items: [
        { label: 'Clients', icon: <Users className="h-[18px] w-[18px]" />, to: '/barrister/clients' },
        { label: 'Documents', icon: <FileText className="h-[18px] w-[18px]" />, to: '/barrister/documents' },
        { label: 'Tasks', icon: <ListTodo className="h-[18px] w-[18px]" />, to: '/barrister/tasks' },
        { label: 'Messages', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/barrister/communications' },
        { label: 'Inbox', icon: <Envelope className="h-[18px] w-[18px]" />, to: '/barrister/inbox' },
      ],
    },
    {
      label: 'Money',
      items: [
        // Time & Expenses is NOT a chambers-coordination feature — a solo barrister records
        // billable time and expenses like anyone else, and the API serves create/edit/convert
        // to silk and junior (activities.routes.ts). The solo nav simply never carried the
        // item, which left /barrister/activities reachable only by typing the URL: a solo
        // barrister had no way to log an expense at all. Matters MORE here than in chambers —
        // there is no clerk to record a cost on their behalf, so an unlogged expense is money
        // the barrister never recovers.
        { label: 'Time & Expenses', icon: <ClockIcon className="h-[18px] w-[18px]" />, to: '/barrister/activities' },
        { label: 'Fees', icon: <Wallet className="h-[18px] w-[18px]" />, to: '/barrister/fees' },
        // A solo barrister has no clerk to chase unpaid fee notes, so the recovery worklist
        // matters MORE here than in chambers, not less.
        ...(canUseRecovery(memberType)
          ? [{ label: 'Recovery', icon: <Warning className="h-[18px] w-[18px]" />, to: '/barrister/recovery' }]
          : []),
        { label: 'Bank', icon: <Bank className="h-[18px] w-[18px]" />, to: '/barrister/bank-accounts' },
        ...(canUseIntakeAndReports(memberType)
          ? [{ label: 'Reports', icon: <BarChart3 className="h-[18px] w-[18px]" />, to: '/barrister/reports' }]
          : []),
      ],
    },
    {
      label: 'Profile',
      items: [
        // The public profile is a solo practitioner's SHOPFRONT — the only professional page
        // many of them have, since there is no chambers website behind them. The chambers nav
        // has always carried it; the solo nav never did, leaving it four steps deep in Settings
        // (My Practice → Public profile → a small "view" link) for the person who needs it most.
        { label: 'Public Profile', icon: <Globe className="h-[18px] w-[18px]" />, to: '/barrister/profile' },
        // Availability IS a solo feature (owner decision, 2026-10-21). The server always granted
        // it — `diary.availability` is PERSONAL scope, read/write self (settings-registry.ts:125)
        // — but the solo nav omitted it and the dashboard shortcut was suppressed, so the only
        // way in was Settings → Diary. A solo barrister may have an assistant to work around
        // their leave, and in any case records it for their own diary.
        { label: 'Availability', icon: <Calendar className="h-[18px] w-[18px]" />, to: '/barrister/availability' },
        { label: 'Settings', icon: <Settings className="h-[18px] w-[18px]" />, to: '/barrister/settings' },
        { label: 'Support', icon: <MessageSquare className="h-[18px] w-[18px]" />, to: '/me/support' },
      ],
    },
  ];
}

/**
 * The nav sections for the signed-in member, already filtered.
 *
 * Shared by the desktop sidebar and the mobile/tablet drawer in `TopBar.tsx`, so the two can
 * never drift apart — the drawer used to carry a hand-written list of five shortcuts and none
 * of the actual menu. The clerk-workspace fetch behind `useClerkWorkspace` is a TanStack Query
 * with a shared key, so a second caller costs no extra request.
 *
 * Filling `useClerkWorkspaceStore` (which `ClerkPermissionGuard` depends on) deliberately stays
 * in `Sidebar`, which `AppShell` mounts on every authenticated page: the drawer only exists
 * while it is open, so that store must not depend on it.
 */
export function useSidebarSections(): NavSection[] {
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const chambersMode = useChambersMode();
  const isBarrister = user?.memberType && ['silk', 'junior', 'pupil', 'door_tenant'].includes(user.memberType);
  const { scopes: clerkScopes } = useClerkWorkspace(isClerk);

  const rawSections = chambersMode.isSolo && isBarrister
    ? getSoloBarristerNav(user?.memberType)
    : getNavForRole(user?.memberType);

  // Filter nav items based on clerk scopes
  return isClerk
    ? rawSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.scope || clerkScopes.includes(item.scope)),
        }))
        .filter((section) => section.items.length > 0)
    : rawSections;
}

interface SidebarNavListProps {
  sections: NavSection[];
  /** Icon-only rail. The drawer always passes false — a drawer has room for labels. */
  collapsed?: boolean;
  /** Called after any link is followed, so the mobile drawer can close itself. */
  onNavigate?: () => void;
  /** Rendered inside the scroll area, after the last section — the drawer's account rows. */
  footer?: React.ReactNode;
  className?: string;
}

/** The nav itself — every section, every link. Rendered by both the sidebar and the drawer. */
export function SidebarNavList({ sections, collapsed = false, onNavigate, footer, className }: SidebarNavListProps) {
  const navRef = useRef<HTMLElement>(null);

  // The nav is longer than a phone screen and always opened at the top, so the page you were
  // already on was usually below the fold: open the drawer from Members and you were looking at
  // Dashboard, having to scroll to find yourself. Bring the current item into view on mount —
  // only when it is actually out of sight, so a desktop sidebar that already shows it stays put.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const current = nav.querySelector<HTMLElement>('[aria-current="page"]');
    if (!current) return;

    const navBox = nav.getBoundingClientRect();
    const itemBox = current.getBoundingClientRect();
    if (itemBox.top >= navBox.top && itemBox.bottom <= navBox.bottom) return;

    nav.scrollTop += itemBox.top - navBox.top - (nav.clientHeight - itemBox.height) / 2;
  }, []);

  return (
    <nav ref={navRef} className={cn('flex-1 overflow-y-auto px-3 py-5', className)}>
      {sections.map((section, i) => (
        <div key={i} className={cn('mb-6', i === 0 && 'mt-0')}>
          {section.label && !collapsed && (
            <div className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.1em] text-ink-400">
              {section.label}
            </div>
          )}
          {collapsed && section.label && (
            <div className="mb-2 flex justify-center">
              <div className="h-px w-6 bg-ink-800" />
            </div>
          )}
          <div className="space-y-1">
            {section.items.map((item) => item.external ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onNavigate}
                className={cn(
                  'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
                  'text-ink-400 hover:bg-ink-800/70 hover:text-ink-100',
                  collapsed && 'justify-center px-0',
                )}
                title={collapsed ? item.label : undefined}
              >
                <span className="shrink-0 transition-colors duration-200 text-ink-500 group-hover:text-ink-300">
                  {item.icon}
                </span>
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge && (
                      <span className="rounded-md bg-brand-600/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-300">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/admin' || item.to === '/clerk' || item.to === '/barrister'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200',
                    isActive
                      ? 'bg-brand-600/15 text-white shadow-sm shadow-brand-500/5'
                      : 'text-ink-400 hover:bg-ink-800/70 hover:text-ink-100',
                    collapsed && 'justify-center px-0',
                  )
                }
                title={collapsed ? item.label : undefined}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-400 shadow-sm shadow-brand-400/30" />
                    )}
                    <span className={cn(
                      'shrink-0 transition-colors duration-200',
                      isActive ? 'text-brand-400' : 'text-ink-500 group-hover:text-ink-300',
                    )}>
                      {item.icon}
                    </span>
                    {!collapsed && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && (
                          <span className="rounded-md bg-brand-600/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-brand-300">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
      {footer}
    </nav>
  );
}

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const chambers = useAuthStore((s) => s.chambers);
  const chambersMode = useChambersMode();
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';

  const isBarrister = user?.memberType && ['silk', 'junior', 'pupil', 'door_tenant'].includes(user.memberType);

  // Real barristers from /api/v1/barristers — pre-warm for barrister-side nav lookups.
  const fetchBarristersList = useApiBarristersStore((s) => s.fetch);
  useEffect(() => {
    if (isBarrister) fetchBarristersList();
  }, [isBarrister, fetchBarristersList]);

  // Clerk workspace — which barristers they support + scopes, all DERIVED from
  // the capabilities engine (GET /permissions/workspace). Single source of truth;
  // replaces the old mock scopesStore.
  const { entries: workspaceEntries, isLoaded: workspaceLoaded } = useClerkWorkspace(isClerk);

  // Populate the clerk workspace store so ClerkPermissionGuard enforces the
  // barrister-granted capabilities. Only set once the fetch has resolved — until
  // then the store stays unloaded and the guard renders optimistically, so a
  // clerk never sees a flash of "Access not granted" while loading.
  const setClerkWorkspaces = useClerkWorkspaceStore((s) => s.setWorkspaces);
  useEffect(() => {
    if (isClerk && workspaceLoaded) {
      setClerkWorkspaces(
        workspaceEntries.map((e) => ({
          barristerId: e.barristerId,
          barristerUserId: e.barristerUserId,
          barristerName: e.barristerName,
          permissions: e.permissions as PermissionCode[],
          permissionsStatus: e.permissionsStatus as 'pending' | 'active',
        })),
      );
    }
  }, [isClerk, workspaceLoaded, workspaceEntries, setClerkWorkspaces]);

  const sections = useSidebarSections();

  return (
    <aside
      className={cn(
        'sticky top-0 flex h-screen shrink-0 flex-col border-r border-ink-800/50 bg-ink-900 transition-all duration-300 ease-in-out',
        collapsed ? 'w-[68px]' : 'w-[260px]',
      )}
    >
      {/* Brand */}
      <div className={cn('flex h-16 items-center border-b border-ink-800/50 px-5', collapsed && 'justify-center px-0')}>
        <div className="flex min-w-0 items-center gap-3">
          <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-lg shadow-brand-600/20">
            <Scale className="h-[18px] w-[18px]" weight="bold" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="truncate text-[15px] font-bold leading-tight tracking-tight text-white">
                Jurivoxa
              </div>
              {/* The workspace type is stated, not implied. The badge is `shrink-0` so a long
                  practice name truncates around it rather than hiding the one word that says
                  which kind of workspace this is. */}
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate text-[10px] font-medium uppercase tracking-[0.1em] text-ink-400">
                  {chambers?.name ?? workspaceTypeLabel(chambersMode.mode)}
                </span>
                <span className="shrink-0 rounded border border-brand-600/30 bg-brand-900/40 px-1 py-px text-[9px] font-semibold uppercase tracking-[0.08em] text-brand-300">
                  {workspaceTypeLabel(chambersMode.mode)}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Acting-for banner (clerk only) */}
      {isClerk && workspaceEntries.length > 0 && !collapsed && (
        <div className="mx-3 mt-3 rounded-md border border-brand-600/30 bg-brand-900/40 px-3 py-2">
          <div className="text-[9px] font-bold uppercase tracking-[0.1em] text-brand-400">
            Acting for {workspaceEntries.length} barrister{workspaceEntries.length > 1 ? 's' : ''}
          </div>
          <div className="mt-1.5 space-y-1.5">
            {workspaceEntries.slice(0, 4).map((entry) => {
              const isPending = entry.permissionsStatus === 'pending';
              const initials = entry.barristerName.split(' ').map((p: string) => p[0]).join('').slice(0, 2);
              return (
                <div key={entry.barristerId} className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-800 text-[7px] font-bold text-brand-300">
                    {initials}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[11px] text-ink-300">
                    {entry.barristerName}
                  </span>
                  {isPending ? (
                    <span className="shrink-0 rounded bg-amber-900/60 px-1 py-0.5 text-[8px] font-semibold text-amber-400">
                      pending
                    </span>
                  ) : (
                    <span className="shrink-0 text-[8px] text-ink-500">
                      {entry.permissions.length}p
                    </span>
                  )}
                </div>
              );
            })}
            {workspaceEntries.length > 4 && (
              <div className="text-[10px] text-ink-500">+{workspaceEntries.length - 4} more</div>
            )}
          </div>
        </div>
      )}
      {isClerk && workspaceEntries.length > 0 && collapsed && (
        <div className="mx-auto mt-3 flex h-8 w-8 items-center justify-center rounded-md bg-brand-900/40" title={`Acting for ${workspaceEntries.length} barristers`}>
          <span className="text-[9px] font-bold text-brand-400">{workspaceEntries.length}</span>
        </div>
      )}

      {/* Nav */}
      <SidebarNavList sections={sections} collapsed={collapsed} />

      {/* Collapse */}
      <div className="border-t border-ink-800/50 p-3">
        <button
          onClick={toggle}
          className={cn(
            'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-ink-400 transition-all duration-200 hover:bg-ink-800/60 hover:text-ink-300',
            collapsed && 'justify-center',
          )}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform duration-300', collapsed && 'rotate-180')} />
          {!collapsed && 'Collapse'}
        </button>
      </div>
    </aside>
  );
}
